import {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useEffect,
} from "react";
import { getNoDataStats } from "@/api/asset-api.js";
import { isRequestAborted } from "@/api/api-utils.js";
import { buildNoDataStatsQuery } from "@/lib/no-data-utils.js";
import { useDataLoaderContext } from "./DataLoaderContext.jsx";
import { useSessionTenant } from "./SessionTenantContext.jsx";

/**
 * @typedef {Object} NoDataStatsCacheEntry
 * @property {Object|null} data
 * @property {boolean} isLoading
 * @property {string|null} error
 * @property {Date|null} lastUpdated
 */

/**
 * @typedef {Object} NoDataStatsContextType
 * @property {(selectedRange: string, customStart: string, customEnd: string, assetId?: string|number) => NoDataStatsCacheEntry} getNoDataStatsForRange
 * @property {(selectedRange: string, customStart: string, customEnd: string, assetId?: string|number) => Promise<Object|null>} fetchNoDataStatsForRange
 * @property {number} statsRefreshToken - Increments when cache is cleared (tenant switch or data-loader interval)
 */

const EMPTY_ENTRY = {
  data: null,
  isLoading: false,
  error: null,
  lastUpdated: null,
};

/** @param {Map<string, NoDataStatsCacheEntry>} cache @param {string} cacheKey @param {Partial<NoDataStatsCacheEntry>} patch */
function patchCacheEntry(cache, cacheKey, patch) {
  const next = new Map(cache);
  next.set(cacheKey, { ...(next.get(cacheKey) ?? EMPTY_ENTRY), ...patch });
  return next;
}

const NoDataStatsContext = createContext(
  /** @type {NoDataStatsContextType|null} */ (null)
);

/** @returns {NoDataStatsContextType} */
export const useNoDataStats = () => {
  const context = useContext(NoDataStatsContext);
  if (!context) {
    throw new Error("useNoDataStats must be used within a NoDataStatsProvider");
  }
  return context;
};

/** Caches no-data stats by preset range, custom dates, and optional asset id. */
export function NoDataStatsProvider({ children }) {
  const cacheRef = useRef(
    /** @type {Map<string, NoDataStatsCacheEntry>} */ (new Map())
  );
  const [, setCacheVersion] = useState(0);
  const [statsRefreshToken, setStatsRefreshToken] = useState(0);
  const inFlightRef = useRef(/** @type {Set<string>} */ (new Set()));
  const { triggerCount } = useDataLoaderContext();
  const { tenantRefreshKey } = useSessionTenant();

  const bumpCache = useCallback((updater) => {
    cacheRef.current = updater(cacheRef.current);
    setCacheVersion((v) => v + 1);
  }, []);

  const invalidateCache = useCallback(() => {
    cacheRef.current = new Map();
    inFlightRef.current = new Set();
    setCacheVersion((v) => v + 1);
    setStatsRefreshToken((n) => n + 1);
  }, []);

  useEffect(() => {
    if (tenantRefreshKey > 0 || triggerCount > 0) {
      invalidateCache();
    }
  }, [tenantRefreshKey, triggerCount, invalidateCache]);

  const getNoDataStatsForRange = useCallback(
    (selectedRange, customStart, customEnd, assetId) => {
      const query = buildNoDataStatsQuery(
        selectedRange,
        customStart,
        customEnd,
        assetId
      );
      if (!query) return { ...EMPTY_ENTRY };
      return cacheRef.current.get(query.cacheKey) ?? { ...EMPTY_ENTRY };
    },
    []
  );

  const fetchNoDataStatsForRange = useCallback(
    async (selectedRange, customStart, customEnd, assetId) => {
      const query = buildNoDataStatsQuery(
        selectedRange,
        customStart,
        customEnd,
        assetId
      );
      if (!query) return null;

      const { cacheKey, dateRange } = query;
      const existing = cacheRef.current.get(cacheKey) ?? EMPTY_ENTRY;

      if (existing.data && !existing.isLoading) {
        return existing.data;
      }

      if (inFlightRef.current.has(cacheKey)) {
        return existing.data;
      }

      inFlightRef.current.add(cacheKey);
      bumpCache((cache) =>
        patchCacheEntry(cache, cacheKey, { isLoading: true, error: null })
      );

      try {
        const data = await getNoDataStats(
          dateRange.FromDate,
          dateRange.ToDate,
          assetId
        );

        bumpCache((cache) =>
          patchCacheEntry(cache, cacheKey, {
            data,
            isLoading: false,
            error: null,
            lastUpdated: new Date(),
          })
        );

        return data;
      } catch (err) {
        if (isRequestAborted(err)) return null;

        console.error("No-data stats error:", err);
        bumpCache((cache) =>
          patchCacheEntry(cache, cacheKey, {
            data: null,
            isLoading: false,
            error: "Failed to load no-data stats",
            lastUpdated: null,
          })
        );
        return null;
      } finally {
        inFlightRef.current.delete(cacheKey);
      }
    },
    [bumpCache]
  );

  /** @type {NoDataStatsContextType} */
  const value = {
    getNoDataStatsForRange,
    fetchNoDataStatsForRange,
    statsRefreshToken,
  };

  return (
    <NoDataStatsContext.Provider value={value}>
      {children}
    </NoDataStatsContext.Provider>
  );
}
